import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { routes } from '../../pages/routes'
import { Fragment, useEffect, useRef } from 'react'
import { DEFAULT_OAUTH_SCOPES, ssoClientConfigMappings } from './config'
import { ssoStorage } from './ssoStorage'
import { generateOAuthState } from './ssoState'
import { fetchSSOClientConfig } from './api'
import type { ClientSSOContext, SSOClientConfig } from './types'
import { FRESH_LOGIN_PARAM } from './constants'

/**
 * Fallback for clients not yet in the server registry: clients sharing one
 * Cognito app client (e.g. the OPAU group) are indistinguishable at the
 * Cognito layer, and the Hosted UI session cookie would silently
 * re-authenticate the previously used account without ever consulting the
 * upstream IdP. For those clients we must clear the Cognito session before
 * starting a new authorize flow.
 */
/** Query keys are treated case-insensitively throughout this flow. */
const deleteParamCaseInsensitive = (params: URLSearchParams, name: string): void => {
  const target = name.toLowerCase()
  Array.from(params.keys())
    .filter((key) => key.toLowerCase() === target)
    .forEach((key) => params.delete(key))
}

const clientSharesCognitoClientId = (clientId: string): boolean =>
  ssoClientConfigMappings.filter((client) => client.clientId && client.clientId === clientId)
    .length > 1

const resolveClientConfig = async (ssoClientName: string): Promise<SSOClientConfig | null> => {
  const remoteClient = await fetchSSOClientConfig(ssoClientName)
  if (remoteClient) return remoteClient
  const localClient = ssoClientConfigMappings.find((client) => client.clientName === ssoClientName)
  if (!localClient) return null
  return {
    ...localClient,
    sharesCognitoClientId: clientSharesCognitoClientId(localClient.clientId || ''),
  }
}

export const SSOLandingPage = () => {
  const { ssoClientNameParam = '' } = useParams()
  const [searchParams] = useSearchParams()
  const ssoClientName = ssoClientNameParam?.toUpperCase()
  const navigate = useNavigate()
  // Dedupe per input (not a plain boolean) so an in-SPA navigation to a
  // different /sso/:client route still starts a new flow, while repeated
  // effect runs for the same input (StrictMode double-invoke, dep identity
  // changes) don't start the async flow twice.
  const lastRunKey = useRef<string | null>(null)

  useEffect(() => {
    const runKey = `${ssoClientName}?${searchParams.toString()}`
    if (lastRunKey.current === runKey) return
    lastRunKey.current = runKey

    const startSSOFlow = async () => {
      const client = ssoClientName ? await resolveClientConfig(ssoClientName) : null
      if (!client?.clientId) {
        navigate(routes.login.path)
        return
      }
      const clientId = client.clientId
      localStorage.clear()
      const paramsObject: { [key in string]: string } = {}
      searchParams.forEach((value, key) => {
        paramsObject[key.toLowerCase()] = value
      })

      const isFreshLogin = paramsObject[FRESH_LOGIN_PARAM.toLowerCase()] === '1'
      const logoutRedirectUri = process.env.REACT_APP_COGNITO_LOGOUT_REDIRECT_URI
      const cognitoAuthUri = process.env.REACT_APP_COGNITO_AUTH_URI
      if (client.sharesCognitoClientId && !isFreshLogin && logoutRedirectUri && cognitoAuthUri) {
        // Hop through the Cognito /logout endpoint to drop any existing Hosted
        // UI session, then resume this landing flow (see App bootstrap) so the
        // subsequent /authorize is guaranteed to federate to the upstream IdP.
        const originalParams = new URLSearchParams(searchParams)
        deleteParamCaseInsensitive(originalParams, FRESH_LOGIN_PARAM)
        ssoStorage.setPendingFreshLogin(ssoClientName, originalParams.toString())
        const logoutUrl = new URL('/logout', cognitoAuthUri)
        logoutUrl.searchParams.set('client_id', clientId)
        logoutUrl.searchParams.set('logout_uri', logoutRedirectUri)
        window.location.href = logoutUrl.toString()
        return
      }

      if (paramsObject.returnto) {
        ssoStorage.setRedirectUrl(`/${paramsObject.returnto}`)
      }
      const metadataParams = new URLSearchParams(searchParams)
      deleteParamCaseInsensitive(metadataParams, FRESH_LOGIN_PARAM)
      const clientSSOContext: ClientSSOContext = {
        externalPatientFetchId:
          paramsObject['eprescriptiondatafeedid'] || paramsObject['patientid'],
        referralId: paramsObject?.['referralid'],
        clientName: client.clientName,
        providerId: client.providerId,
        metadata: Object.fromEntries(metadataParams.entries()),
        providerPlatform: client.providerPlatform,
        providerType: client.providerType,
        resolvedConfig: client,
      }
      ssoStorage.setContext(clientSSOContext)
      const baseUrl = `${process.env.REACT_APP_COGNITO_AUTH_URI}/authorize`
      const redirectUri = `${process.env.REACT_APP_COGNITO_REDIRECT_URI}/${clientId}`
      const params = new URLSearchParams({
        response_type: 'code',
        identity_provider: client.identityProvider || '',
        client_id: clientId,
        scope: DEFAULT_OAUTH_SCOPES.join(' '),
        state: generateOAuthState(ssoClientName),
        redirect_uri: redirectUri,
      })
      window.location.href = `${baseUrl}?${params.toString()}`
    }

    startSSOFlow()
  }, [searchParams, navigate, ssoClientName])
  return <Fragment />
}
